import {
    ISelection,
    IText,
    ISentence,
    IWord,
    IMediaFormat,
    IMetaData,
    IExportGlossary,
    IQuestionEntry,
    IExportDictionary,
    IWordTranslations,
    WordFormatType,
    TextFormatType,
    IVocabularyEntry
} from "../api/data-model";
import {UtilitiesService} from "../services/utilities.service";


/**
 * List of all CSS Classes we might add to content elements
 */

export enum ContentCssClass {
    MODE_PREVIEW = <any>"preview-mode",
    MODE_READ_TO_ME = <any>"read-to-me-mode",
    MODE_RECORDING = <any>"recording-mode",
    MODE_RECORDING_INTERVENTION = <any>"recording-intervention-mode",
    MODE_RECORDING_DONE = <any>"recording-done-mode",
    MODE_RECORDING_PAUSED = <any>"recording-paused-mode",
    MODE_PLAY_MY_READING = <any>"play-my-reading-mode",
    MODE_QUIZ = <any>"quiz-mode",
    OBJECT_SELECTED = <any>"selected",
    SELECTION = <any>"selection",
    SENTENCE = <any>"sentence",
    SENTENCE_CURRENT = <any>"sentence-current",
    SENTENCE_CURRENT_OPENBOOK = <any>"sentence-current-open-book",
    SENTENCE_RECORDED = <any>"sentence-recorded",
    SENTENCE_UNRECORDED = <any>"sentence-unrecorded",
    TEXT = <any>"text",
    TEXT_CURRENT = <any>"text-current",
    WORD = <any>"word",
    WORD_CURRENT = <any>"word-current",
    WORD_CURRENT_GLOSSARY = <any>"word-current-glossary",
    WORD_GREAT_JOB = <any>"word-great-job",
    WORD_NEEDS_WORK = <any>"word-needs-work",
    WORD_RECOGNIZED = <any>"word-recognized",
    WORD_TRY_AGAIN = <any>"word-try-again",
    WORD_INTERVENTION_VISUAL = <any>"word-intervention-visual"
}

export class CssObject {

    public static SELECTED_CLASS: ContentCssClass;

    public id: string;

    public userSelected:boolean = false;

    protected _parent: CssObject;
    public get parent(): CssObject {
        return this._parent;
    }

    // // TODO: placeholder replace with theme, reading level, and formats CSS rules
    // static makeStyle(format: ITextFormat, zoom: number): Object {
    //     return {
    //         "font-family": format.fontName,
    //         "font-size": zoom * parseFloat(format.fontSize) + "px",
    //         "line-height": format.paragraphLineSpacing + "em",
    //         "font-style": "normal",
    //         "font-weight": "normal"
    //     };
    // }

    constructor(public classes: Object, public formats: Object, public type?: string, id?: string) {
        this.id = id || UtilitiesService.newUuid();
    }

    public hasCssClass(className: ContentCssClass): boolean {
        return this.classes[className] === true;
    };

    public addCssClass(className: ContentCssClass): void {
        this.classes[className] = true;
    };

    public removeCssClass(className: ContentCssClass): void {
        this.classes[className] = false;
    };

    public select(selected = true): void {
        let myClass = <typeof CssObject>this.constructor;
        selected ? this.addCssClass(myClass.SELECTED_CLASS) : this.removeCssClass(myClass.SELECTED_CLASS);
    }

    public isSelected(): boolean {
        let myClass = <typeof CssObject>this.constructor;
        return this.hasCssClass(myClass.SELECTED_CLASS);
    }

    private getElement(): Element {
        return document.getElementById(this.id);
    }

    public getRect(): ClientRect {
        return this.getElement().getBoundingClientRect();
    }

    public getRectBottom(): number {
        return this.getRect().bottom;
    }

    public getRectTop(): number {
        return this.getRect().top;
    }
}

// noinspection JSAnnotator
export class CssSelection extends CssObject implements ISelection {
    public text: CssText[];
    public metaData: IMetaData;
    public pronunciationDictionary: IExportDictionary;
    public glossaryEntries: IExportGlossary;
    public questionEntries: IQuestionEntry[];
    public vocabularyEntries: IVocabularyEntry[];

    constructor(selection: ISelection = <ISelection>{
        text: [],
        metaData: <IMetaData>{},
        pronunciationDictionary: <IExportDictionary>{},
        glossaryEntries: <IExportGlossary>{},
        questionEntries: [],
        vocabularyEntries: []
    }) {
        super({}, {}, "selection");

        this.text = selection.text.map((t: IText) => new CssText(t, this));
        this.metaData = selection.metaData;
        this.pronunciationDictionary = selection.pronunciationDictionary;
        this.glossaryEntries = selection.glossaryEntries;
        this.questionEntries = selection.questionEntries;
        this.vocabularyEntries = selection.vocabularyEntries;
        this.applyThemeToSelectionClasses();
        if (!!this.metaData.ATOSGradeEquivalent) {
            this.applyGradeLevelStyleClasses(UtilitiesService.determineATOSLevel(this.metaData));
        }
    }

    public applyGradeLevelStyleClasses(grade: number): void {
        this.classes["k-3"] = false;
        this.classes["four-five"] = false;
        this.classes["six-eight"] = false;
        this.classes["nine-twelve"] = false;
        if (grade < 4.0) {
            this.classes["k-3"] = true;
        } else if (grade < 6.0) {
            this.classes["four-five"] = true;
        } else if (grade < 9.0) {
            this.classes["six-eight"] = true;
        } else {
            this.classes["nine-twelve"] = true;
        }
    }

    private applyThemeToSelectionClasses(): void {
        if (!!this.metaData.theme) {
            this.classes[this.metaData.theme] = this.metaData.theme;
        }
    }

    public firstChild(): CssText {
        return this.text.length > 0 ? this.text[0] : undefined;
    }

    public firstNonImageChild(): CssText {
        for (let i = 0; i < this.text.length; i++) {
            if (this.text[i].type !== "image") {
                return this.text[i];
            }
        }
        return undefined;
    }

    public nextChild(child: CssText): CssText {
        if (!child) {
            return this.firstChild();
        }
        let index: number = this.text.indexOf(child);
        return (index > -1 && index < this.text.length - 1) ? this.text[index + 1] : undefined;
    }

    public contains(child: CssObject): boolean {
        if (child instanceof CssText && this.text.indexOf(child) > -1) {
            return true;
        } else {
            for (let text of this.text) {
                if (text.contains(<CssSentence|CssWord>child)) {
                    return true;
                }
            }
        }
        return false;
    }

    public nextWord(current: CssWord): CssWord {
        let next: CssWord, parent: CssSentence;
        if (current) {
            parent = current.parentSentence();
            next = parent.nextChild(current);
        } else {
            parent = this.nextSentence(null);
            next = parent.firstChild();
        }

        while (parent && !next) {
            parent = this.nextSentence(parent);
            next = parent ? parent.firstChild() : undefined;
        }

        return next;
    }

    public nextSentence(current: CssSentence): CssSentence {
        let next: CssSentence, parent: CssText;
        if (current) {
            parent = current.parentText();
            if (parent) {
                next = parent.nextChild(current);
            }
        } else {
            parent = this.nextText(null);
            next = parent.firstChild();
        }

        while (parent && !next) {
            parent = this.nextText(parent);
            next = parent ? parent.firstChild() : undefined;
        }

        return next;
    }

    public previousSentence(current: CssSentence): CssSentence {
        let previous: CssSentence = this.nextSentence(null);
        while (previous && this.nextSentence(previous) !== current) {
            previous = this.nextSentence(previous);
        }
        return previous;
    }

    public previousText(current: CssText): CssText {
        let previous: CssText = this.nextText(null);
        while (previous && this.nextText(previous) !== current) {
            previous = this.nextText(previous);
        }
        return previous;
    }

    public sentenceFromId(id: string): CssSentence {
        let sentence = this.nextSentence(null);
        while (sentence) {
            if (sentence.id === id) {
                return sentence;
            }
            sentence = this.nextSentence(sentence);
        }
        return null;
    }

    public nextText(current: CssText): CssText {
        return this.nextChild(current);
    }

    public dictionary(): IExportDictionary {
        return this.pronunciationDictionary;
    }

    public wpmGoal():number {
        return ((typeof this.metaData.wpmGoal) === "string") ? parseInt(this.metaData.wpmGoal, 10) :
               <any>this.metaData.wpmGoal;
    }

    public referenceStory():string {
        return this.metaData.referenceStory;
    }
}

// noinspection JSAnnotator
export class CssText extends CssObject implements IText {

    public static SELECTED_CLASS: ContentCssClass = ContentCssClass.TEXT_CURRENT;
    private static DEFAULT_ZOOM: number = 1.0;

    public sentences: CssSentence[];
    public mediaFormat: IMediaFormat;
    public formats: TextFormatType[];

    constructor(text: IText, parent: CssSelection) {

        super({text: true}, {});

        this._parent = parent;
        this.mediaFormat = text.mediaFormat;
        this.sentences = [];
        this.formats = text.formats;
        if (text.sentences) {
            for (let sentence of text.sentences) {
                this.sentences.push(new CssSentence(sentence, this));
            }
            this.type = "sentences";
        }
        this.applyImageProperties();
        this.convertFormatsToClasses();
    }

    private applyImageProperties(): void {
        if (this.mediaFormat) {
            this.type = "image";
            this.classes["image-block"] = true;
        }
    }

    private convertFormatsToClasses(): void {
        if (this.formats) {
            this.formats.forEach((format) => this.classes[format] = true);
        }
    }

    public isImage(): boolean {
        return this.type === "image";
    }

    public parentSelection(): CssSelection {
        return <CssSelection>this._parent;
    }

    public firstChild(): CssSentence {
        return this.sentences.length > 0 ? this.sentences[0] : undefined;
    }

    public nextChild(child: CssSentence): CssSentence {
        if (!child) {
            return this.firstChild();
        }
        let index: number = this.sentences.indexOf(child);
        return (index > -1 && index < this.sentences.length - 1) ? this.sentences[index + 1] : undefined;
    }

    public contains(child: CssSentence|CssWord): boolean {
        if (child instanceof CssSentence && this.sentences.indexOf(child) > -1) {
            return true;
        } else {
            for (let sentence of this.sentences) {
                if (sentence.contains(<CssWord>child)) {
                    return true;
                }
            }
        }
        return false;
    }

    public adjustFontSize(zoom: number): void {
        // this.styles = CssObject.makeStyle(CssText.DEFAULT_FORMAT, zoom);
    }

    public sentenceForWord(word: CssWord): CssSentence {
        for (let sentence of this.sentences) {
            if (sentence.contains(word)) {
                return sentence;
            }
        }
        return null;
    }
}

export class CssSentence extends CssObject implements ISentence {

    public static SELECTED_CLASS: ContentCssClass = ContentCssClass.SENTENCE_CURRENT;

    public audio: string;
    public words: CssWord[];

    // placeholder for now - probably eventually will be via a css class?
    public inView: boolean;

    constructor(sentence: ISentence, parent: CssText) {
        super({sentence: true}, {}, "sentence", sentence.id);

        this._parent = parent;
        let cssWords: CssWord[] = [];
        for (let word of sentence.words) {
            cssWords.push(new CssWord(word, this));
        }
        this.words = cssWords;
        this.audio = sentence.audio;

        this.inView = false;
        this.convertFormatsToClasses();
    }

    private convertFormatsToClasses(): void {
        if (this.formats) {
            Object.keys(this.formats).forEach((format) => this.classes[format] = true);
        }
    }

    public parentText(): CssText {
        return <CssText>this._parent;
    }

    public firstChild(): CssWord {
        return this.words.length > 0 ? this.words[0] : undefined;
    }

    public nextChild(child: CssWord): CssWord {
        if (!child) {
            return this.firstChild();
        }
        let index: number = this.words.indexOf(child);
        return (index > -1 && index < this.words.length - 1) ? this.words[index + 1] : undefined;
    }

    public previousChild(child: CssWord): CssWord {
        if (!child) {
            return undefined;
        }
        let index: number = this.words.indexOf(child);
        return (index > 0 && index < this.words.length) ? this.words[index - 1] : undefined;
    }

    public contains(child: CssWord): boolean {
        return (this.words.indexOf(child) > -1);
    }

    public indexOfChild(child: CssWord): number {
        return (this.words.indexOf(child));
    }

    public getChildFromId(id: string): CssWord {
        for (let index = 0; index < this.words.length; index++) {
            if (this.words[index].id === id) {
                return this.words[index];
            }
        }

        return undefined;
    }

    public highlightOpenBook(selected = true): void {
        if (selected) {
            this.addCssClass(ContentCssClass.SENTENCE_CURRENT_OPENBOOK);
        } else {
            this.removeCssClass(ContentCssClass.SENTENCE_CURRENT_OPENBOOK);
        }
    }

    public dictionary(): IExportDictionary {
        return this.parentText() && this.parentText().parentSelection() ?
               this.parentText().parentSelection().dictionary() : null;
    }

    public wcpmGoal(): string {
        return this.parentText() && this.parentText().parentSelection() ?
               this.parentText().parentSelection().metaData.wpmGoal : "";
    }
}

export class CssWord extends CssObject implements IWord {

    public static SELECTED_CLASS: ContentCssClass = ContentCssClass.WORD_CURRENT;

    public audio: string;
    public prefix: string;
    public word: string;
    public separator: string;
    public separatorPunctuation: string;
    public separatorTrailingWhiteSpace: string;
    public category: string;
    public glossary: string;
    public dictionary: string[];
    public startQuestionId: string;
    public questionId: string;
    public formats: WordFormatType[];
    public translations:IWordTranslations;

    constructor(word: IWord, parent: CssSentence) {

        let cssClass = {word: true, glossary: !!word.glossary};
        super(cssClass, {}, "word", word.id);

        this._parent = parent;

        // copy from Word
        this.audio = word.audio;
        this.prefix = word.prefix ? word.prefix : "";
        this.word = word.word;
        this.separator = word.separator ? word.separator : "";
        // the separator can contain both punctuation and whitespace. Separate these for better
        // light bulb layout and to avoid problems with punctuation and lightbulb wrapping to the following line
        this.separatorPunctuation = this.separator.replace(/\s+$/, "$'");
        this.separatorTrailingWhiteSpace = this.separator.replace(this.separatorPunctuation, "");
        this.category = word.category;
        this.questionId = word.questionId;
        this.glossary = word.glossary;
        this.dictionary = word.dictionary;
        this.startQuestionId = word.startQuestionId;
        this.questionId = word.questionId;
        this.formats = word.formats;
        this.translations = word.translations;
        this.convertFormatsToClasses(cssClass);
    }

    private convertFormatsToClasses(cssClass: Object): Object {
        if (this.formats) {
            this.formats.forEach((format: string) => { cssClass[format] = true; });
        }
        return cssClass;
    }

    public parentSentence(): CssSentence {
        return <CssSentence>this._parent;
    }

    public parentText(): CssText {
        return (<CssSentence>this._parent).parentText();
    }

    public selectGlossaryPhrase(select: boolean): void {

        let word: CssWord = this;
        let glossaryId: string = this.glossary;
        this.highlightGlossary(select);
        while (this.parentSentence().nextChild(word) &&
               (this.parentSentence().nextChild(word).glossary === glossaryId)) {
            word = this.parentSentence().nextChild(word);
            word.highlightGlossary(select);
        }
        while (this.parentSentence().previousChild(word) &&
               (this.parentSentence().previousChild(word).glossary === glossaryId)) {
            word = this.parentSentence().previousChild(word);
            word.highlightGlossary(select);
        }

    }

    public highlightGlossary(selected = true): void {
        if (selected) {
            this.addCssClass(ContentCssClass.WORD_CURRENT_GLOSSARY);
        } else {
            this.removeCssClass(ContentCssClass.WORD_CURRENT_GLOSSARY);
        }
    }

    // different word categories affect the logic for things like recognition processing and word color coding
    // "glue" words - common functional words like the, a, to
    public isGlue(): boolean {
        return (this.category === "1");
    }

    // default category = everything not in one of the other categories - just a regular word!
    public isDefault(): boolean {
        return (this.category === "2");
    }

    // "target" words (important or new vocabulary)
    public isTarget(): boolean {
        return (this.category === "3");
    }

    // special words are foreign words or unusual names - user not necessarily expected to know how to read
    public isSpecial(): boolean {
        return (this.category === "4");
    }

    // default and target words are required (to be heard in recognition); glue and special words are not
    public isRequired(): boolean {
        return (this.category === "2" || this.category === "3");
    }

    // prosodic punctuation is punctuation you would expect the user to pause at in expressive reading
    public hasProsodicPunctuation(): boolean {
        return !!this.separator.match(/[?,;:.!—]/);
    }
}
